import React, { useCallback, useEffect, useRef, useState } from "react";
import { Key } from "ts-key-enum";
import { t } from "ttag";

import { IOptionCode, IProduct } from "../../../models/catalogue";
import { IProductID } from "../../../models/nominals";
import { useAppSelector } from "../../../store/reducers";
import { intersection } from "../../../utils/sets";
import { TabFeatureAttributeBlockModelTabButton } from "../TabFeatureAttributeBlockModelTabButton";
import { TabFeatureAttributeBlockModelTabContent } from "../TabFeatureAttributeBlockModelTabContent";
import { TabFeatureAttributeBlockModelTabImage } from "../TabFeatureAttributeBlockModelTabImage";
import { tabFeatureAttributeBlock } from "../elementIDs";
import { ITabFeatureAttributeBlock_AttributeTab } from "../models";
import { rootProductsSelector } from "../selectors";

import styles from "./index.module.scss";

type TabFeatureAttributeBlockAttributeTabContentProps = {
    attribute: IOptionCode;
    idx: number;
    tab: ITabFeatureAttributeBlock_AttributeTab;
    isSelected: boolean;
};

export const TabFeatureAttributeBlockAttributeTabContent: React.FC<
    TabFeatureAttributeBlockAttributeTabContentProps
> = ({ attribute, idx, tab, isSelected }) => {
    const rootProducts = useAppSelector((state) =>
        rootProductsSelector(state.configurator),
    );
    const [selectedModel, setSelectedModel] = useState<IProductID | null>(null);

    // Helper to get selected root product from redux
    const getSelectedRootProductFromRedux = (
        rootProducts: readonly IProduct[],
    ): IProductID | null => {
        if (rootProducts.length <= 0) {
            return null;
        }
        const tabPIDs = new Set(tab.tabs.map((t) => t.model));
        const selectedPIDs = new Set(rootProducts.map((p) => p.id));
        const selectedPIDsWithTabs = intersection(tabPIDs, selectedPIDs);
        if (selectedPIDsWithTabs.size <= 0) {
            return null;
        }
        return [...selectedPIDsWithTabs][0];
    };

    useEffect(() => {
        const reduxSelectedModel =
            getSelectedRootProductFromRedux(rootProducts);
        const next = reduxSelectedModel ?? tab.tabs[0]?.model ?? null;
        setSelectedModel((prev) => (prev === next ? prev : next));
    }, [rootProducts, tab.tabs]);

    const handleClick = (model: IProductID) => {
        setSelectedModel((prev) => (prev === model ? prev : model));
    };

    const modelTabRefs = useRef<(HTMLButtonElement | null)[]>([]);

    const handleModelTabKeyDown = useCallback(
        (e: React.KeyboardEvent, modelIndex: number) => {
            const count = tab.tabs.length;
            if (count === 0) return;

            if (e.key === Key.ArrowLeft) {
                e.preventDefault();
                const prevIndex = modelIndex > 0 ? modelIndex - 1 : count - 1;
                const prev = modelTabRefs.current[prevIndex];
                prev?.focus();
                prev?.click();
            }
            if (e.key === Key.ArrowRight) {
                e.preventDefault();
                const nextIndex = modelIndex < count - 1 ? modelIndex + 1 : 0;
                const next = modelTabRefs.current[nextIndex];
                next?.focus();
                next?.click();
            }
            if (e.key === Key.Home && modelTabRefs.current[0]) {
                e.preventDefault();
                const first = modelTabRefs.current[0];
                first.focus();
                first.click();
            }
            if (e.key === Key.End && modelTabRefs.current[count - 1]) {
                e.preventDefault();
                const last = modelTabRefs.current[count - 1];
                last?.focus();
                last?.click();
            }
        },
        [tab.tabs.length],
    );

    // Compute active model tab once
    const activeModelTab = selectedModel
        ? tab.tabs.find((t) => t.model === selectedModel) || tab.tabs[0]
        : tab.tabs[0];

    return (
        <article
            className={styles.root}
            id={tabFeatureAttributeBlock.attributeTabContent(tab, idx)}
            role="tabpanel"
            aria-labelledby={tabFeatureAttributeBlock.attributeTabButton(
                tab,
                idx,
            )}
            hidden={!isSelected}
        >
            <div className={styles.modelGraphic}>
                {activeModelTab && (
                    <TabFeatureAttributeBlockModelTabImage
                        key={String(activeModelTab.model)}
                        attrIdx={idx}
                        attrTab={tab}
                        modelIdx={tab.tabs.indexOf(activeModelTab)}
                        modelTab={activeModelTab}
                        isSelected={true}
                    />
                )}
            </div>
            <div className={styles.modelButtonsAndContent}>
                <div
                    className={styles.modelButtons}
                    role="tablist"
                    aria-label={t`Model options`}
                >
                    {tab.tabs.map((modelTab, i: number) => (
                        <TabFeatureAttributeBlockModelTabButton
                            key={String(modelTab.model)}
                            ref={(el) => {
                                modelTabRefs.current[i] = el;
                            }}
                            attrIdx={idx}
                            attrTab={tab}
                            modelIdx={i}
                            modelTab={modelTab}
                            onClick={() => handleClick(modelTab.model)}
                            isSelected={
                                modelTab.model === activeModelTab?.model
                            }
                            onKeyDown={(e) => handleModelTabKeyDown(e, i)}
                        />
                    ))}
                </div>
                <div className={styles.modelContent}>
                    {activeModelTab && (
                        <TabFeatureAttributeBlockModelTabContent
                            key={`${activeModelTab.model}`}
                            attribute={attribute}
                            attrIdx={idx}
                            attrTab={tab}
                            modelIdx={tab.tabs.indexOf(activeModelTab)}
                            modelTab={activeModelTab}
                            isSelected={true}
                        />
                    )}
                </div>
            </div>
        </article>
    );
};
